import queryString from 'query-string'

const isRawBody = body =>
  typeof body === 'string' || body instanceof FormData || body instanceof Blob || body instanceof URLSearchParams

const formatMethod = rawMethod => {
  const method = rawMethod.toUpperCase()
  return method === 'DEL' ? 'DELETE' : method
}

export const isAbortError = error => error?.name === 'AbortError'

const baseUrl = () => (process.env.REACT_APP_API_ENDPOINT || '').replace(/\/+$/, '')

const buildUrl = (path, queryStringParameters) => {
  let url = `${baseUrl()}/${path.replace(/^\/+/, '')}`
  if (queryStringParameters && Object.keys(queryStringParameters).length > 0) {
    const filtered = {}
    for (const [key, raw] of Object.entries(queryStringParameters)) {
      if (raw === undefined || raw === null) continue
      filtered[key] = raw
    }
    const qs = queryString.stringify(filtered)
    if (qs) {
      url += url.includes('?') ? `&${qs}` : `?${qs}`
    }
  }
  return url
}

const parseErrorBody = async response => {
  const contentType = response.headers.get('content-type') || ''
  if (contentType.includes('application/json')) {
    try {
      return await response.json()
    } catch {
      return null
    }
  }
  try {
    return await response.text()
  } catch {
    return null
  }
}

const attachBody = (init, method, body) => {
  if (body === undefined || body === null || method.toLowerCase() === 'get') {
    return
  }

  if (isRawBody(body)) {
    // fetch must set multipart Content-Type itself so the boundary is included
    init.body = body
    return
  }

  init.headers['Content-Type'] = init.headers['Content-Type'] || 'application/json'
  init.body = JSON.stringify(body)
}

/**
 * Drop-in replacement factory for Amplify v4 `API`.
 *
 * @param {object} options
 * @param {() => Promise<string|undefined|null>} options.getAuthorizationToken
 * @param {'always'|'whenPresent'} [options.authorization='always'] — always set Authorization header, or only when token is truthy
 * @param {string} [options.apiName='civicChampsApi']
 */
export function createRestApi({
  getAuthorizationToken,
  authorization = 'always',
  apiName: expectedApiName = 'civicChampsApi',
}) {
  const request = async (method, apiName, path, config = {}) => {
    if (apiName !== expectedApiName) {
      throw new Error(`Unknown API: ${apiName}`)
    }

    const { body, headers = {}, queryStringParameters, responseType, signal } = config

    const authorizationToken = await getAuthorizationToken()
    const init = {
      method: formatMethod(method),
      headers: { ...headers },
      signal,
    }

    if (authorization === 'whenPresent') {
      if (authorizationToken) {
        init.headers.Authorization = authorizationToken
      }
    } else {
      init.headers.Authorization = authorizationToken
    }

    attachBody(init, method, body)

    let response
    try {
      response = await fetch(buildUrl(path, queryStringParameters), init)
    } catch (error) {
      // requestWithRetry retries 'Network Error', so an abort must keep its own identity
      if (isAbortError(error)) {
        throw error
      }
      throw new Error('Network Error')
    }

    if (!response.ok) {
      const data = await parseErrorBody(response)
      const error = new Error(`${formatMethod(method)} ${path} failed: ${response.status}`)
      error.response = { status: response.status, data }
      throw error
    }

    if (responseType === 'blob') {
      return response.blob()
    }

    if (response.status === 204) {
      return null
    }

    const contentType = response.headers.get('content-type') || ''
    if (contentType.includes('application/json')) {
      return response.json()
    }

    return response.text()
  }

  return {
    get: (name, path, config) => request('get', name, path, config),
    post: (name, path, config) => request('post', name, path, config),
    put: (name, path, config) => request('put', name, path, config),
    patch: (name, path, config) => request('patch', name, path, config),
    del: (name, path, config) => request('del', name, path, config),
  }
}
